// State machine, types, and Phase-1 mock data for the multi-step cancellation
// flow (FLOCK-6549). Pure TypeScript — no Vue dependency — so the screen graph
// stays unit-testable and the Phase-2 live-data swap is isolated to loadUsageStats().
//
// Copy and design tokens are sourced from Figma file 7TFXFVb7oRLXL8VlzcJeON.

import moment from "moment";

// ---------------------------------------------------------------------------
// Screens + paths
// ---------------------------------------------------------------------------

export enum CancelScreen {
  Intercept = "intercept", // Screen 0 — usage stats (+ fallback variant)
  Confirm = "confirm", // Screen 1 — effective date + value props
  Feedback = "feedback", // Screen 2 — reason checkboxes + "Other"
  OfferAnnual = "offerAnnual", // Screen 3a — 3 months free
  OfferAnnualEscalated = "offerAnnualEscalated", // Screen 3b — 50% off
  OfferMonthly = "offerMonthly", // Screen 3c — 1 month free
  Finalize = "finalize", // Screen 4 — final confirm -> real cancel
  AcceptedAnnual = "acceptedAnnual", // Screen A — annual offer claimed
  AcceptedMonthly = "acceptedMonthly" // Screen B — monthly offer claimed
}

export type CancelPath = "annual" | "monthly" | "po";

// Intent passed to goNext() from the offer screens.
export type CancelIntent = "accept" | "decline";

// ---------------------------------------------------------------------------
// Path resolution
// ---------------------------------------------------------------------------

// Resolve the user's path from real billing context. PO skips all retention
// offers; annual vs monthly is driven by the Stripe billing interval.
export function resolvePath(opts: {
  billingInterval?: string;
  isPO?: boolean;
}): CancelPath {
  if (opts.isPO) return "po";
  return opts.billingInterval === "month" ? "monthly" : "annual";
}

// Dev affordances below are inert on the production host: the query toggles
// reveal the Cancel button and force paths/mock data, which must never be
// reachable by a customer.
function devTogglesAllowed(): boolean {
  try {
    return !/(^|\.)goditto\.com$/i.test(window.location.hostname);
  } catch {
    return false;
  }
}

// Dev affordance: `?cancelMock=annual|monthly|po` forces a path so all flows can
// be reviewed from a single test account. Returns null when not set/invalid.
export function getForcedPath(): CancelPath | null {
  if (!devTogglesAllowed()) return null;
  try {
    const v = new URLSearchParams(window.location.search).get("cancelMock");
    if (v === "annual" || v === "monthly" || v === "po") return v;
  } catch {
    // URLSearchParams unavailable — ignore
  }
  return null;
}

// Dev affordance: reveal the Cancel button even without an active paid subscription
// (e.g. on a trial) so the flow can be fired for review. Triggered by `?cancelMock=...`
// or `?cancelDev=1`. Returns false in normal use, so production gating is unchanged.
export function isCancelDevMode(): boolean {
  if (!devTogglesAllowed()) return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.has("cancelMock") || params.get("cancelDev") === "1";
  } catch {
    return false;
  }
}

// Dev affordance: `?cancelStats=none` forces Screen 0's fallback (no-stats) variant.
export function forceStatsUnavailable(): boolean {
  if (!devTogglesAllowed()) return false;
  try {
    return (
      new URLSearchParams(window.location.search).get("cancelStats") === "none"
    );
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Forward transition table
// ---------------------------------------------------------------------------

// Returns the next screen given the current screen, path, an optional intent
// (only meaningful on the offer screens), and whether the account has already
// used its one-time retention offer. Back navigation is handled by a history
// stack in the container, so no reverse edges are encoded.
//
// Order follows the source specs (FLOCK-6285 monthly / FLOCK-6290 yearly /
// FLOCK-6292 HubSpot): Intercept -> Confirm -> Offer(s) -> [decline] -> Feedback
// -> Finalize. Feedback is the final gate before cancellation, reached only
// after declining all offers (or directly for PO / offer-already-used accounts).
export function nextScreen(
  current: CancelScreen,
  path: CancelPath,
  intent?: CancelIntent,
  offerAlreadyUsed = false
): CancelScreen {
  switch (current) {
    case CancelScreen.Intercept:
      return CancelScreen.Confirm;
    case CancelScreen.Confirm:
      // PO and accounts that already used their lifetime offer skip retention.
      if (path === "po" || offerAlreadyUsed) return CancelScreen.Feedback;
      return path === "annual"
        ? CancelScreen.OfferAnnual
        : CancelScreen.OfferMonthly;
    case CancelScreen.OfferAnnual:
      return intent === "accept"
        ? CancelScreen.AcceptedAnnual
        : CancelScreen.OfferAnnualEscalated;
    case CancelScreen.OfferAnnualEscalated:
      return intent === "accept"
        ? CancelScreen.AcceptedAnnual
        : CancelScreen.Feedback;
    case CancelScreen.OfferMonthly:
      return intent === "accept"
        ? CancelScreen.AcceptedMonthly
        : CancelScreen.Feedback;
    case CancelScreen.Feedback:
      return CancelScreen.Finalize;
    default:
      // Finalize / Accepted screens are terminal.
      return current;
  }
}

// Terminal screens whose primary button completes the flow rather than advancing.
export function isTerminal(screen: CancelScreen): boolean {
  return (
    screen === CancelScreen.Finalize ||
    screen === CancelScreen.AcceptedAnnual ||
    screen === CancelScreen.AcceptedMonthly
  );
}

// ---------------------------------------------------------------------------
// Feedback reasons (Screen 2)
// ---------------------------------------------------------------------------

// Reason IDs are the intended HubSpot enum keys for ditto_cancel_modal_reason.
// Final mapping pending design confirmation; the IDs are best-guess snake_case
// derivatives of the Figma labels and may need a 1:1 remap before Phase 2 wiring.
export interface CancelReason {
  id: string;
  label: string;
}

export const CANCEL_REASONS: CancelReason[] = [
  { id: "too_expensive", label: "Too expensive" },
  { id: "technical_difficulties", label: "Technical difficulties" },
  { id: "not_using", label: "Not using the service" },
  { id: "mirroring_quality", label: "Mirroring quality" },
  { id: "lack_of_features", label: "Lack of features" },
  { id: "moving_to_other", label: "Moving to another solution" },
  { id: "other", label: "Other" }
];

// ---------------------------------------------------------------------------
// Retention offers (Screens 3a/3b/3c)
// ---------------------------------------------------------------------------

export type OfferType = "annual" | "annualEscalated" | "monthly";

export interface RetentionOffer {
  type: OfferType;
}

// The API owns the offer -> Stripe coupon mapping (api-service
// controllers/subscription.ts CANCELLATION_COUPON_IDS); the client only names
// the offer it is claiming. See FLOCK-6281.
export const OFFERS: Record<OfferType, RetentionOffer> = {
  annual: { type: "annual" },
  annualEscalated: { type: "annualEscalated" },
  monthly: { type: "monthly" }
};

// The slice of a Stripe subscription the accepted screens read back.
export interface IDiscountedSubscription {
  discount?: {
    coupon?: {
      name?: string | null;
      percent_off?: number | null;
      amount_off?: number | null;
      currency?: string | null;
    } | null;
  } | null;
}

// Human label for the discount Stripe reports back after a coupon is applied.
export function describeDiscount(
  subscription: IDiscountedSubscription | null | undefined
): string {
  const coupon = subscription && subscription.discount && subscription.discount.coupon;
  if (!coupon) return "";
  if (coupon.name) return coupon.name;
  if (coupon.percent_off) return `${coupon.percent_off}% off`;
  if (coupon.amount_off) {
    const amount = (coupon.amount_off / 100).toFixed(2);
    return `${(coupon.currency || "usd").toUpperCase()} ${amount} off`;
  }
  return "Discount applied";
}

// ---------------------------------------------------------------------------
// Usage stats (Screen 0) — mock layer
// ---------------------------------------------------------------------------

export interface CancelUsageStats {
  connectionCount: number | null;
  mirroringHours: number | null;
  signageCount: number | null;
}

// Per-path mock values mirror the original Figma stub fixtures.
const MOCK_STATS: Record<CancelPath, CancelUsageStats> = {
  annual: { connectionCount: 42, mirroringHours: 21, signageCount: 8 },
  monthly: { connectionCount: 18, mirroringHours: 6, signageCount: 3 },
  po: { connectionCount: 124, mirroringHours: 58, signageCount: 22 }
};

const EMPTY_STATS: CancelUsageStats = {
  connectionCount: null,
  mirroringHours: null,
  signageCount: null
};

// Phase 1: returns mock stats (or empty when the dev toggle forces the fallback).
// Dev-only stats provider, used when ?cancelMock / ?cancelDev is set so the flow
// can be demoed without real data. The real trailing-12-month fetch (StatState)
// lives in the container's fetchRealStats(); this just returns the mock fixtures.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function loadUsageStats(
  _organizationId: string,
  path: CancelPath
): Promise<CancelUsageStats> {
  if (forceStatsUnavailable()) return { ...EMPTY_STATS };
  return { ...MOCK_STATS[path] };
}

// Drives Screen 0's variant. Requires the two reliably-available metrics
// (connections + mirroring hours via the stats query). Signage count comes
// from GET /api/subscriptions/cancellation-stats and is rendered only when
// above zero.
// Zero usage counts as unavailable: "0 connections this past year" argues
// for cancelling, not against it (Nisreen, 2026-09-08), so a brand-new or
// idle org sees the value-proposition variant instead of the numbers.
export function statsAreAvailable(stats: CancelUsageStats | null): boolean {
  if (!stats || stats.connectionCount == null || stats.mirroringHours == null) return false;
  return stats.connectionCount > 0 && stats.mirroringHours > 0;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

// current_period_end is a Unix epoch in seconds (Stripe). Format to a friendly
// date, matching how Billing.vue renders expiration dates via moment.
export function formatPeriodEnd(
  currentPeriodEnd: number | null | undefined
): string {
  if (!currentPeriodEnd) return "the end of your billing period";
  return moment
    .utc(currentPeriodEnd * 1000)
    .local()
    .format("LL");
}
